/**
 * Guidance and tone palettes for earnings-call highlights, shared cross-tree.
 *
 * Imported by BOTH the on-screen panel (`src/components/earnings/CallHighlights.jsx`) and the
 * briefing email renderer (`workers/earnings-transcript-cron/src/email.js`). The two surfaces
 * describe the same call, so a guidance pill that is green in the app and grey in the email reads
 * as a bug about the data rather than about the CSS.
 *
 * It lives in `src/shared/` because the dependency direction is forced: CRA cannot import from
 * outside `src/`, but wrangler/esbuild can import into it — the same arrangement as
 * `bandSignals.js` and `sentimentBands.js`.
 *
 * `text` is the human label; `bg`/`fg` are the pill colours. The email needs the label spelled out
 * (there is no tooltip in an inbox), the panel renders it too, so one table carries both. `short`
 * is the stock table's Guidance cell (issue #306), whose column header already says "Guidance".
 * `bar` is the saturated fill for the season bars (issue #387, desktop card + mobile Brief) —
 * the pill `fg` is too dark to read as a fill, so a fill colour sits beside it in the ONE palette.
 */

export const GUIDANCE_STYLE = {
  raised: { bg: '#dcfce7', fg: '#166534', bar: '#059669', text: 'Guidance raised', short: 'Raised' },
  lowered: { bg: '#fee2e2', fg: '#991b1b', bar: '#e11d48', text: 'Guidance lowered', short: 'Lowered' },
  maintained: { bg: '#f1f5f9', fg: '#475569', bar: '#94a3b8', text: 'Guidance maintained', short: 'Maintained' },
  initiated: { bg: '#e0f2fe', fg: '#075985', bar: '#0ea5e9', text: 'Guidance initiated', short: 'Initiated' },
};

/**
 * Severity drives the badge colour on threats. The worker ranks the list, so this is purely a
 * visual weight cue, not the ordering mechanism.
 */
export const SEVERITY_STYLE = {
  high: { bg: '#fee2e2', fg: '#991b1b' },
  medium: { bg: '#fef3c7', fg: '#92400e' },
  low: { bg: '#f1f5f9', fg: '#475569' },
};

export const TONE_STYLE = {
  confident: { bg: '#dcfce7', fg: '#166534' },
  cautious: { bg: '#fef3c7', fg: '#92400e' },
  defensive: { bg: '#fee2e2', fg: '#991b1b' },
  mixed: { bg: '#f1f5f9', fg: '#475569' },
};

/** Shown at the foot of every surface that renders machine-generated call analysis. */
export const CALL_DISCLAIMER =
  'Auto-generated from the call audio — speech recognition can misrender names and figures. ' +
  "Verify anything you trade on against the company's own filing.";
